// ============================================================
// Gap Detection Engine — Phase 3 core algorithm
// ============================================================
import { GAP_TYPES, LIMITATION_PHRASES } from '../data/gapKeywords';

/**
 * Normalise text: lowercase, remove punctuation, collapse whitespace.
 */
export function normaliseText(text = '') {
  return text
    .toLowerCase()
    .replace(/[''""]/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Count occurrences of a phrase inside a body of text.
 */
function countOccurrences(phrase, text) {
  const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`\\b${escaped}\\b`, 'gi');
  return (text.match(regex) || []).length;
}

/**
 * Detect gap signals in a single abstract text.
 * Returns { gapScores, limitationPhrases, matchedKeywords }
 */
export function detectGapsInAbstract(abstract = '') {
  const text = normaliseText(abstract);
  const gapScores = {};
  const matchedKeywords = {};

  Object.entries(GAP_TYPES).forEach(([type, { keywords }]) => {
    let score = 0;
    const matched = [];
    keywords.forEach((kw) => {
      const count = countOccurrences(kw, text);
      if (count > 0) {
        score += count;
        matched.push({ phrase: kw, count });
      }
    });
    gapScores[type] = score;
    matchedKeywords[type] = matched;
  });

  const foundLimitationPhrases = LIMITATION_PHRASES.filter((p) =>
    text.includes(p)
  );

  return { gapScores, matchedKeywords, foundLimitationPhrases };
}

/**
 * Process all articles.
 * articles = [ { id, title, authors, year, abstract, limitations } ]
 * keywords = string[] (user-supplied)
 */
export function processAllArticles(articles = [], keywords = []) {
  const normKeywords = keywords.map(normaliseText);

  const results = articles.map((article) => {
    const combined = normaliseText(
      [article.abstract, article.limitations].filter(Boolean).join(' ')
    );

    const detection = detectGapsInAbstract(combined);

    // Keyword relevance boost: if a user keyword appears in the abstract,
    // add a small bonus ONLY to the gap type whose keyword dictionary
    // already detected at least one signal — keeps differentiation intact.
    const finalScores = {};
    Object.keys(GAP_TYPES).forEach((t) => {
      let bonus = 0;
      if (detection.gapScores[t] > 0) {
        // Count how many user keywords appear in this abstract
        const kwHits = normKeywords.filter((kw) => combined.includes(kw)).length;
        bonus = kwHits * 0.3; // max ~3 pts for 10 keywords — keeps it proportionate
      }
      finalScores[t] = detection.gapScores[t] + bonus;
    });

    // Dominant gap type for this article
    const dominant = Object.entries(finalScores).sort((a, b) => b[1] - a[1])[0];

    return {
      ...article,
      gapScores: finalScores,
      matchedKeywords: detection.matchedKeywords,
      foundLimitationPhrases: detection.foundLimitationPhrases,
      dominantGapType: dominant[0],
      dominantScore: dominant[1],
      confidenceScore: calcConfidence(finalScores),
    };
  });

  return results;
}

/**
 * Aggregate gap scores across all articles → ranked list.
 */
export function aggregateGapScores(processedArticles = []) {
  const totals = {};
  const articleCounts = {};

  Object.keys(GAP_TYPES).forEach((t) => {
    totals[t] = 0;
    articleCounts[t] = 0;
  });

  processedArticles.forEach((article) => {
    Object.keys(GAP_TYPES).forEach((t) => {
      totals[t] += article.gapScores[t] || 0;
      if ((article.gapScores[t] || 0) > 0) articleCounts[t]++;
    });
  });

  const ranked = Object.entries(totals)
    .map(([type, score]) => ({
      type,
      label: GAP_TYPES[type].label,
      color: GAP_TYPES[type].color,
      totalScore: Math.round(score * 10) / 10,
      articleCount: articleCounts[type],
      percentage:
        processedArticles.length > 0
          ? Math.round((articleCounts[type] / processedArticles.length) * 100)
          : 0,
    }))
    .sort((a, b) => b.totalScore - a.totalScore);

  return ranked;
}

/**
 * Simple confidence score 0-100 based on signal density.
 */
function calcConfidence(scores) {
  const max = Math.max(...Object.values(scores));
  if (max === 0) return 0;
  if (max >= 10) return 95;
  if (max >= 7) return 85;
  if (max >= 4) return 75;
  if (max >= 2) return 60;
  return 40;
}

/**
 * Build the Limitations Matrix table data.
 */
export function buildLimitationsMatrix(processedArticles = []) {
  return processedArticles.map((a) => ({
    id: a.id,
    citation: `${a.authors} (${a.year})`,
    title: a.title,
    dominantGap: GAP_TYPES[a.dominantGapType]?.label || 'Unknown',
    dominantGapType: a.dominantGapType,
    keyPhrase: a.foundLimitationPhrases[0] || '—',
    confidenceScore: a.confidenceScore,
    allScores: a.gapScores,
  }));
}

/**
 * Generate gap warnings based on analysis.
 */
export function generateGapWarnings(ranked = [], processedArticles = []) {
  const warnings = [];

  // Check for weak signals
  if (ranked[0]?.totalScore < 2) {
    warnings.push({
      type: 'LOW_SIGNAL',
      severity: 'warning',
      message:
        'Gap signals are weak across all abstracts. Consider adding more recent articles (last 2 years) or articles with explicit limitation sections.',
    });
  }

  // Check for dominance spread
  const top = ranked[0]?.totalScore || 0;
  const second = ranked[1]?.totalScore || 0;
  if (top > 0 && second / top < 0.3) {
    warnings.push({
      type: 'SINGLE_DOMINANT',
      severity: 'info',
      message: `${ranked[0]?.label} strongly dominates. Verify this reflects the true gap or widen your abstract selection.`,
    });
  }

  // PhD-level caution
  warnings.push({
    type: 'ACADEMIC_LEVEL',
    severity: 'info',
    message:
      'For PhD research, ensure the identified gap represents intellectual innovation beyond contextual or methodological competence alone.',
  });

  // Recent literature warning
  const years = processedArticles.map((a) => parseInt(a.year, 10)).filter(Number.isFinite);
  const currentYear = new Date().getFullYear();
  if (years.length > 0) {
    const maxYear = Math.max(...years);
    if (maxYear < currentYear - 2) {
    warnings.push({
      type: 'OUTDATED_LITERATURE',
      severity: 'danger',
        message: `Your most recent article is from ${maxYear}. Ensure the gap has not already been filled by more recent studies (${currentYear - 1}–${currentYear}).`,
      });
    }
  }

  return warnings;
}
